// biome-ignore lint/complexity/noStaticOnlyClass: keeping usage as class for consistency
export class ThemeEvents {
  static variantSelected = 'variant:selected'
  static variantUpdate = 'variant:update'
  static cartUpdate = 'cart:update'
  static cartError = 'cart:error'
  static cartDrawerOpen = 'cart:drawer-open'
  static mediaStartedPlaying = 'media:started-playing'
  static quantitySelectorUpdate = 'quantity-selector:update'
  static discountUpdate = 'discount:update'
  static filterUpdate = 'filter:update'
  static refresh = 'component:refresh'
  static scrollLockChanged = 'theme:scroll-lock:changed'
}

const scrollLockClassName = 'nav-is-open'
const activeScrollLockSources = new Set()

function getScrollLockSource(source) {
  if (typeof source === 'string' && source.trim()) {
    return source
  }

  return 'global'
}

function hasScrollLockDetail(event) {
  return (
    event.detail !== null &&
    typeof event.detail === 'object' &&
    typeof event.detail.locked === 'boolean'
  )
}

function applyScrollLockDetail(detail) {
  const source = getScrollLockSource(detail.source)

  if (detail.locked) {
    activeScrollLockSources.add(source)
  } else {
    activeScrollLockSources.delete(source)
  }

  document.documentElement.classList.toggle(
    scrollLockClassName,
    activeScrollLockSources.size > 0
  )
}

function handleScrollLockChanged(event) {
  if (!hasScrollLockDetail(event)) return

  applyScrollLockDetail(event.detail)
}

export function on(eventName, callback, options, target = document) {
  target.addEventListener(eventName, callback, options)
  return () => target.removeEventListener(eventName, callback, options)
}

export class VariantSelectedEvent extends Event {
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true })
    this.detail = {
      resource
    }
  }
}

export class VariantUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true })
    this.detail = {
      data: {
        html: data.html,
        newProduct: data.newProduct,
        productId: data.productId
      },
      resource: resource || null,
      sourceId
    }
  }
}

export class CartAddEvent extends Event {
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true })
    this.detail = {
      data: {
        ...data,
        changeType: data?.changeType ?? 'add'
      },
      isRewardAction: data?.isRewardAction,
      preventAutoOpen: data?.preventAutoOpen,
      resource,
      sourceId
    }
  }

  static eventName = ThemeEvents.cartUpdate
}

export class CartUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true })
    this.detail = {
      data: {
        ...data,
        changeType: data?.changeType ?? 'update'
      },
      isRewardAction: data?.isRewardAction,
      preventAutoOpen: data?.preventAutoOpen,
      resource,
      sourceId
    }
  }
}

export class CartErrorEvent extends Event {
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true })
    this.detail = {
      data: {
        description,
        errors,
        message
      },
      sourceId
    }
  }
}

export class QuantitySelectorUpdateEvent extends Event {
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true })
    this.detail = {
      cartLine,
      quantity
    }
  }
}

export class DiscountUpdateEvent extends Event {
  constructor(resource, sourceId, data) {
    super(ThemeEvents.discountUpdate, { bubbles: true })
    this.detail = {
      morphed: data?.morphed,
      preventAutoOpen: data?.preventAutoOpen,
      resource,
      sourceId
    }
  }
}

export class MediaStartedPlayingEvent extends Event {
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true })
    this.detail = {
      resource
    }
  }
}

export class SlideshowSelectEvent extends Event {
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true })
    this.detail = data
  }

  detail

  static eventName = 'slideshow:select'
}

export class FilterUpdateEvent extends Event {
  constructor(queryParams) {
    super(ThemeEvents.filterUpdate, { bubbles: true })
    this.detail = {
      queryParams
    }
  }

  shouldShowClearAll() {
    return (
      [...this.detail.queryParams.entries()].filter(([key]) =>
        key.startsWith('filter.')
      ).length > 0
    )
  }
}

export class ScrollLockChangedEvent extends CustomEvent {
  constructor(locked, source = 'global') {
    super(ThemeEvents.scrollLockChanged, {
      bubbles: true,
      detail: {
        locked: Boolean(locked),
        source: getScrollLockSource(source)
      }
    })
  }
}

/**
 * Dispatches the shared document scroll-lock event and tracks each lock source.
 *
 * @example
 * syncScrollLock(true, 'dialog')
 */
export function syncScrollLock(locked, source = 'global') {
  const nextSource = getScrollLockSource(source)
  const nextLocked = Boolean(locked)

  if (activeScrollLockSources.has(nextSource) === nextLocked) return
  document.dispatchEvent(new ScrollLockChangedEvent(nextLocked, nextSource))
}

document.addEventListener(
  ThemeEvents.scrollLockChanged,
  handleScrollLockChanged
)

export class RefreshEvent extends CustomEvent {
  constructor(detail = {}) {
    super(ThemeEvents.refresh, {
      bubbles: true,
      composed: true,
      detail: {
        newContent: detail.newContent || null,
        targetElement: detail.targetElement || null
      }
    })
  }
}
